import type { Dispatch, SetStateAction } from "react";
import type { IFinancialsTemplateFieldsFragment } from "components/financials/modern/graphql/fragments/__generated__/FinancialsTemplateFieldsFragment.generated";
import type { ISecurityCountry } from "types/graphqlTypes";
import { IFinancialsTemplateVisibility, IIndicatorDataSource, IPlatformVertical } from "types/graphqlTypes";
import { Provider } from "jotai";
import DefineTemplateForm from "components/financials/modern/templates/DefineTemplateForm";
import { useTemplateActions } from "components/financials/modern/templates/hooks/useTemplateActions";
import useViewerCountry from "lib/hooks/useViewerCountry";

interface DefineTemplateFormProps {
  isCreatingTemplate: boolean;
  editingTemplate: IFinancialsTemplateFieldsFragment | null;
  indicatorDataSource: IIndicatorDataSource;
  financialsTemplate: IFinancialsTemplateFieldsFragment | null;

  onCancel: () => void;
  onCompleted: () => void;
  setIsCreatingTemplate: Dispatch<SetStateAction<boolean>>;
  setEditingTemplate: Dispatch<SetStateAction<IFinancialsTemplateFieldsFragment | null>>;
}

export default function TemplateSheetContentForm({
  isCreatingTemplate,
  editingTemplate,
  indicatorDataSource,
  financialsTemplate,
  onCancel,
  onCompleted,
  setIsCreatingTemplate,
  setEditingTemplate,
}: DefineTemplateFormProps) {
  const { addFinancialsTemplate, editFinancialsTemplate } = useTemplateActions();
  const country = useViewerCountry();

  return (
    <>
      {editingTemplate && (
        <Provider>
          <DefineTemplateForm
            filters={{
              filterByIndicatorVertical: null,
              filterByDataSource: [
                indicatorDataSource,
                IIndicatorDataSource.Global,
                IIndicatorDataSource.Manual,
                IIndicatorDataSource.Other,
                IIndicatorDataSource.Unspecified,
              ],
            }}
            defaultValues={{
              name: editingTemplate.name,
              isGlobalForVerticals: editingTemplate.vertical === IPlatformVertical.All,
              description: editingTemplate.description,
              indicators: editingTemplate.indicators,
            }}
            onCancel={onCancel}
            onSubmit={({ name, isGlobalForVerticals, description, indicators }) => {
              if (!financialsTemplate) {
                return;
              }

              editFinancialsTemplate({
                variables: {
                  objectId: editingTemplate.objectId,
                  input: {
                    templateCodes: financialsTemplate.templateCodes,
                    name,
                    description,
                    indicators: indicators.map(({ objectId }) => objectId),
                    vertical: isGlobalForVerticals ? IPlatformVertical.All : financialsTemplate.vertical,
                    visibility: editingTemplate.visibility,
                    countryCode: country as ISecurityCountry,
                    dataSource: indicatorDataSource,
                    betterSuitedFor: [],
                    overrideCriteria: null,
                  },
                },
                onCompleted,
              });
              setEditingTemplate(null);
            }}
          />
        </Provider>
      )}

      {isCreatingTemplate && (
        <Provider>
          <DefineTemplateForm
            filters={{
              filterByIndicatorVertical: null,
              filterByDataSource: [
                indicatorDataSource,
                IIndicatorDataSource.Global,
                IIndicatorDataSource.Manual,
                IIndicatorDataSource.Other,
                IIndicatorDataSource.Unspecified,
              ],
            }}
            defaultValues={{
              name: "",
              description: "",
              isGlobalForVerticals: false,
              indicators: [],
            }}
            onCancel={() => setIsCreatingTemplate(false)}
            onSubmit={({ name, isGlobalForVerticals, description, indicators }) => {
              if (!financialsTemplate) {
                return;
              }

              setIsCreatingTemplate(false);
              addFinancialsTemplate({
                variables: {
                  input: {
                    templateCodes: financialsTemplate.templateCodes,
                    name,
                    description,
                    indicators: indicators.map(({ objectId }) => objectId),
                    vertical: isGlobalForVerticals ? IPlatformVertical.All : financialsTemplate.vertical,
                    visibility: IFinancialsTemplateVisibility.Private,
                    countryCode: country as ISecurityCountry,
                    dataSource: indicatorDataSource,
                    betterSuitedFor: [],
                    overrideCriteria: null,
                  },
                },
                onCompleted,
              });
            }}
          />
        </Provider>
      )}
    </>
  );
}
